import React from 'react';
import PropTypes from 'prop-types';
import ConfigCard from './ConfigCard';
import { OrderedChipList } from './_PdiHelpers';

// All sections the storefront knows how to render on the product page. Each
// section is either IN USE (chip in the ordered list) or AVAILABLE.
//
// NICE-TO-HAVE: a 'washInstructions' block. The storefront only renders 8
// blocks today (header, sizeSelector, addToCart, locationsStock, variantsList,
// related, productInfo, extraText) via PRODUCT_DETAILS_ORDER. Adding wash
// instructions requires a storefront-side <section> + a new component, then
// reintroducing the chip here.
const DEFAULT_BLOCKS = [
    { key: 'header',          i18nKey: 'cfg_pdi_layout_block_header',          fallback: 'Header',           used: true  },
    { key: 'sizeSelector',    i18nKey: 'cfg_pdi_layout_block_size_selector',   fallback: 'Size selector',    used: true  },
    { key: 'addToCart',       i18nKey: 'cfg_pdi_layout_block_add_to_cart',     fallback: 'Add to cart',      used: true  },
    { key: 'stockByLocation', i18nKey: 'cfg_pdi_layout_block_stock_location',  fallback: 'Stock by location',used: true  },
    { key: 'variants',        i18nKey: 'cfg_pdi_layout_block_variants',        fallback: 'Variants',         used: true  },
    { key: 'relatedProducts', i18nKey: 'cfg_pdi_layout_block_related',         fallback: 'Related products', used: true  },
    { key: 'productInfo',     i18nKey: 'cfg_pdi_layout_block_product_info',    fallback: 'Product info',     used: true  },
    { key: 'extraText',       i18nKey: 'cfg_pdi_layout_block_extra_text',      fallback: 'Extra text',       used: true  },
];

const PageLayoutCard = ({ value = {}, onChange, theme, t }) => {
    const tr = (key, fallback) => (typeof t === 'function' ? t(key) : fallback);
    const blocks = value.blocks || {};
    // PRODUCT_DETAILS_ORDER sequence (see the load/save in config/): the chips
    // follow it, and dragging them rewrites it.
    const order = Array.isArray(value.order) ? value.order : [];
    const rank = (k) => { const i = order.indexOf(k); return i === -1 ? order.length + DEFAULT_BLOCKS.findIndex(b => b.key === k) : i; };

    const items = DEFAULT_BLOCKS.map(b => ({
        key:   b.key,
        label: tr(b.i18nKey, b.fallback),
        used:  blocks[b.key]?.used !== undefined ? blocks[b.key].used : b.used,
    })).sort((a, b) => rank(a.key) - rank(b.key));

    const setUsed = (k, v) => {
        if (!onChange) return;
        onChange('blocks', { ...blocks, [k]: { used: v } });
        // an added section goes to the end of the page
        if (v) onChange('order', [...items.map(it => it.key).filter(x => x !== k), k]);
    };
    // new order of the used sections; the unused ones keep their place after them
    const reorder = (usedKeys) => onChange && onChange('order', [
        ...usedKeys, ...items.map(it => it.key).filter(k => !usedKeys.includes(k)),
    ]);

    return (
        <ConfigCard
            anchor="cfg-card-pdi-layout"
            title={tr('cfg_pdi_layout_title', 'Page layout')}
            description={tr('cfg_pdi_layout_desc', 'Which sections appear on the product page and in what order. Drag chips to reorder, or click an available field below to add it.')}
            theme={theme}
        >
            <OrderedChipList
                orderedLabel={tr('cfg_pdi_layout_used',      'Sections used (in order)')}
                availableLabel={tr('cfg_pdi_layout_available','Available (click to add)')}
                items={items}
                setUsed={setUsed}
                onReorder={reorder}
            />
        </ConfigCard>
    );
};

PageLayoutCard.propTypes = {
    value:    PropTypes.object,
    onChange: PropTypes.func,
    theme:    PropTypes.string,
    t:        PropTypes.func,
};

export default PageLayoutCard;
